import { BuildingOffice2Icon, UserIcon, CheckCircleIcon, XCircleIcon, InformationCircleIcon, DocumentTextIcon, EyeIcon, PencilSquareIcon, TrashIcon, EnvelopeIcon, PhoneIcon, DocumentPlusIcon, UserGroupIcon, PlusIcon, LinkIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { customerDisplayName } from "../../utils/customerName";
import { SimpleOrganization } from "pages/Customers";
import { useLanguage } from "../../contexts/LanguageContext";
import { Customer } from "../../types/customer";
import CustomerContactStatus from "./CustomerContactStatus";
import { Tooltip } from "../common/Tooltip";
import { customerToKeyValuePairs } from '../../utils/customerDataMapper';
import { useState, useEffect } from 'react';
import { useCustomerGroups } from '../../hooks/useCustomerGroups';
import { useCustomFields } from '../../hooks/useCustomFields';
import toast from 'react-hot-toast';

interface OrganizationAddButtonProps {
  customer: Customer;
  organizations: SimpleOrganization[];
  onAddOrganization: (customer: Customer, orgId: string) => void;
}

const OrganizationAddButton: React.FC<OrganizationAddButtonProps> = ({ customer, organizations, onAddOrganization }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const { t } = useLanguage();

  const availableOrgs = organizations.filter(org => 
    !customer.organizations?.includes(org.id)
  );

  if (availableOrgs.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-gray-600 dark:text-gray-300 border border-dashed border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <PlusIcon className="w-3 h-3" />
        <span>{t('customers.tooltips.addOrg')}</span>
      </button>
      
      {showDropdown && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-[100]" 
            onClick={() => setShowDropdown(false)}
          />
          {/* Dropdown */}
          <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-auto z-[101] mt-1 sm:w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-64 overflow-y-auto">
            {availableOrgs.map(org => (
              <button
                key={org.id}
                onClick={() => {
                  onAddOrganization(customer, org.id);
                  setShowDropdown(false);
                }}
                className="block w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 first:rounded-t-md last:rounded-b-md"
              >
                <div className="flex items-center">
                  <BuildingOffice2Icon className="w-4 h-4 mr-2" />
                  {org.name}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Group Add Button Component
interface GroupAddButtonProps {
  customer: Customer;
}

const GroupAddButton: React.FC<GroupAddButtonProps> = ({ customer }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const { groups, addCustomers } = useCustomerGroups();

  const handleAddToGroup = async (groupId: string) => {
    setShowDropdown(false);
    
    // Firebase hívás - hook automatikusan frissít a háttérben
    addCustomers(groupId, [customer.id]).then(() => {
      toast.success('Ügyfél hozzáadva a csoporthoz');
    }).catch((error: any) => {
      toast.error(`Hiba: ${error.message}`);
    });
  };

  // Csak azok a csoportok, amelyekhez még nincs hozzáadva az ügyfél
  const availableGroups = groups.filter(group => 
    !group.customerIds.includes(customer.id)
  );

  if (availableGroups.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-gray-600 dark:text-gray-300 border border-dashed border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <PlusIcon className="w-3 h-3" />
        <span>Csoport</span>
      </button>
      
      {showDropdown && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-[100]" 
            onClick={() => setShowDropdown(false)}
          />
          {/* Dropdown */}
          <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-auto z-[101] mt-1 sm:w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-64 overflow-y-auto">
            {availableGroups.map(group => (
              <button
                key={group.id}
                onClick={() => handleAddToGroup(group.id)}
                className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 first:rounded-t-md last:rounded-b-md"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded flex items-center justify-center text-sm flex-shrink-0"
                    style={{ backgroundColor: group.color || '#3B82F6' + '20' }}
                  >
                    {group.icon || '👥'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {group.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {group.customerCount} ügyfél
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};


const displayName = customerDisplayName;

/** Több, vesszővel elválasztott telefonszám: az elsőt mutatjuk, a többit +N jelzi. */
const splitPhones = (phone?: string): string[] =>
  (phone || '').split(/[,;]+/).map(p => p.trim()).filter(Boolean);

const PhoneCell: React.FC<{ phone?: string; className?: string; withIcon?: boolean }> = ({ phone, className = '', withIcon }) => {
  const phones = splitPhones(phone);
  if (phones.length === 0) return null;
  return (
    <span className={`inline-flex items-center gap-1 min-w-0 ${className}`} title={phones.join(' · ')}>
      {withIcon && <PhoneIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />}
      <a href={`tel:${phones[0]}`} className="truncate tabular-nums hover:text-primary-600 dark:hover:text-primary-400">{phones[0]}</a>
      {phones.length > 1 && (
        <span className="flex-shrink-0 text-[11px] px-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 tabular-nums">+{phones.length - 1}</span>
      )}
    </span>
  );
};

interface CustomerListProps {
  customers: Customer[];
  organizations: SimpleOrganization[];
  onStatusChange: (customer: Customer) => void;
  onViewClick: (customer: Customer) => void;
  onEditClick: (customer: Customer) => void;
  onDeleteClick: (customer: Customer) => void;
  onDocumentClick: (customer: Customer) => void;
  onGenerateDocument: (customer: Customer) => void;
  onAddOrganization: (customer: Customer, orgId: string) => void;
  onRemoveOrganization: (customer: Customer, orgId: string) => void;
  onSendInvitation?: (customer: Customer) => void;
  // Bulk select props
  selectedCustomerIds?: string[];
  onSelectCustomer?: (customerId: string) => void;
  onSelectAll?: () => void;
  showBulkSelect?: boolean;
}

const CustomerList: React.FC<CustomerListProps> = ({
  customers,
  organizations,
  onStatusChange,
  onViewClick,
  onEditClick,
  onDeleteClick,
  onDocumentClick,
  onGenerateDocument,
  onAddOrganization,
  onRemoveOrganization,
  onSendInvitation,
  selectedCustomerIds = [],
  onSelectCustomer,
  onSelectAll,
  showBulkSelect = false
}) => {
  const { t } = useLanguage();
  const { groups, removeCustomers } = useCustomerGroups();
  const { customFields } = useCustomFields('customer');

  const handleGenerateDocument = (customer: Customer) => {
    onGenerateDocument(customer);
  };

  return (
    <>
      {/* Responsive táblázat - újratervezett, kártyákként mobil nézetben */}
      <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {showBulkSelect && (
                  <th className="px-3 py-2 w-12">
                    <input
                      type="checkbox"
                      checked={customers.length > 0 && selectedCustomerIds.length === customers.length}
                      onChange={onSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                    />
                  </th>
                )}
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('customers.fields.name')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('customers.fields.contact')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('customers.fields.organizations')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  Csoportok
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('customers.fields.status')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('customers.fields.notes')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300 w-20">
                  {t('customers.fields.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {customers.map(customer => (
                <tr key={customer.id} onDoubleClick={() => onViewClick(customer)} title="Dupla kattintás: megtekintés" className="hover:bg-gray-50 dark:hover:bg-gray-700 select-none">
                  {showBulkSelect && (
                    <td className="px-3 py-2 w-12">
                      <input
                        type="checkbox"
                        checked={selectedCustomerIds.includes(customer.id)}
                        onChange={() => onSelectCustomer?.(customer.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                      />
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <div className="flex items-center">
                      <div className="p-1.5 rounded bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-700 border mr-2">
                        {customer.type === 'organization' ? (
                          <BuildingOffice2Icon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                        ) : (
                          <UserIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => onViewClick(customer)}
                            className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-primary-700 dark:hover:text-primary-300 hover:underline text-left"
                            title={t('customers.actions.view')}
                          >
                            {displayName(customer)}
                          </button>
                          {customer.registrationSource === 'public_link' && (
                            <Tooltip content={t('customers.tooltips.publicRegistration') || 'Nyilvános regisztrációs linken keresztül regisztrált'}>
                              <div className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700">
                                <LinkIcon className="w-3 h-3 mr-1" />
                                Link
                              </div>
                            </Tooltip>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {customer.type === 'organization' && customer.taxNumber && customer.taxNumber}
                        </div>
                        {/* Custom Fields */}
                        {customFields.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {customFields.map(field => {
                              const value = customer.customFields?.[field.key];
                              if (value === undefined || value === null || value === '') return null;
                              
                              return (
                                <div key={field.id} className="text-xs text-gray-600 dark:text-gray-400">
                                  <span className="font-medium">{field.name}:</span>{' '}
                                  {Array.isArray(value) ? (
                                    <span className="inline-flex flex-wrap gap-1">
                                      {value.map((val: string, idx: number) => (
                                        <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300">
                                          {val}
                                        </span>
                                      ))}
                                    </span>
                                  ) : field.type === 'checkbox' ? (
                                    <span>{value ? '✓' : '✗'}</span>
                                  ) : field.type === 'date' ? (
                                    <span>{new Date(value).toLocaleDateString('hu-HU')}</span>
                                  ) : (
                                    <span>{String(value)}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col">
                      {customer.email && (
                        <a href={`mailto:${customer.email}`} className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400">
                          {customer.email}
                        </a>
                      )}
                      <PhoneCell phone={customer.phone} className="text-sm text-gray-600 dark:text-gray-400" />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {customer.organizations?.map(orgId => {
                        const org = organizations.find((o: SimpleOrganization) => o.id === orgId);
                        return org ? (
                          <div
                            key={org.id}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700 group"
                          >
                            <BuildingOffice2Icon className="w-3 h-3 mr-1" />
                            {org.name}
                            <button
                              onClick={() => onRemoveOrganization(customer, orgId)}
                              className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <XCircleIcon className="w-3 h-3 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" />
                            </button>
                          </div>
                        ) : null;
                      })}
                      <OrganizationAddButton 
                        customer={customer}
                        organizations={organizations}
                        onAddOrganization={onAddOrganization}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {groups.filter(group => group.customerIds.includes(customer.id)).map(group => (
                        <div
                          key={group.id}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border group"
                          style={{ 
                            backgroundColor: group.color ? group.color + '20' : '#3B82F620',
                            borderColor: group.color || '#3B82F6',
                            color: group.color || '#3B82F6'
                          }}
                        >
                          <span className="mr-1">{group.icon || '👥'}</span>
                          {group.name}
                          <button
                            onClick={() => {
                              // Firebase hívás - hook automatikusan frissít
                              removeCustomers(group.id, [customer.id]).then(() => {
                                toast.success('Ügyfél eltávolítva a csoportból');
                              }).catch((error: any) => {
                                toast.error(`Hiba: ${error.message}`);
                              });
                            }}
                            className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <XCircleIcon className="w-3 h-3 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" />
                          </button>
                        </div>
                      ))}
                      <GroupAddButton customer={customer} />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      <button
                        onClick={() => onStatusChange(customer)}
                        className={`p-1.5 rounded-full ${customer.status === 'active'
                          ? 'text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20'
                          : 'text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20'
                          }`}
                      >
                        {customer.status === 'active' ? (
                          <CheckCircleIcon className="h-5 w-5" />
                        ) : (
                          <XCircleIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      {customer.notes ? (
                        <Tooltip content={customer.notes}>
                          <InformationCircleIcon
                            className="h-5 w-5 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 cursor-help transition-colors"
                          />
                        </Tooltip>
                      ) : (
                        <InformationCircleIcon
                          className="h-5 w-5 text-gray-400 dark:text-gray-500"
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end space-x-2">
                      <Tooltip content="Kapcsolattartók kezelése">
                        <div>
                          <CustomerContactStatus 
                            customer={customer} 
                            onClick={() => onSendInvitation?.(customer)}
                          />
                        </div>
                      </Tooltip>
                      
                      {customer.documents && customer.documents.length > 0 && (
                        <Tooltip content={t('customers.tooltips.viewDocuments') || 'View Documents'}>
                          <button
                            onClick={() => onDocumentClick(customer)}
                            className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                          </button>
                        </Tooltip>
                      )}

                      <Tooltip content={t('customers.tooltips.view')}>
                        <button
                          onClick={() => onViewClick(customer)}
                          className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>

                      <Tooltip content={t('customers.tooltips.edit')}>
                        <button
                          onClick={() => onEditClick(customer)}
                          className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content={t('customers.tooltips.delete')}>
                        <button
                          onClick={() => onDeleteClick(customer)}
                          className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobil nézet - lista/kártya elrendezés */}
      <div className="lg:hidden space-y-3 mb-2 w-full min-w-0">
        {customers.map(customer => {
          const isSelected = selectedCustomerIds.includes(customer.id);
          const customerGroups = groups.filter(group => group.customerIds.includes(customer.id));
          const initial = (customer.type === 'organization' ? customer.companyName : customer.lastName)?.charAt(0)?.toUpperCase() || '?';
          const stop = (e: React.SyntheticEvent) => { e.stopPropagation(); };
          return (
            <div
              key={customer.id}
              role="button"
              tabIndex={0}
              onClick={() => onViewClick(customer)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onViewClick(customer); } }}
              className={`crm-card w-full min-w-0 overflow-hidden cursor-pointer active:bg-gray-50 dark:active:bg-gray-800/60 transition-colors ${isSelected ? 'border-primary-400 dark:border-primary-500' : ''}`}
            >
              {/* Fejléc: kijelölés, monogram, név, státusz */}
              <div className="flex items-start gap-3 px-3 pt-3">
                {showBulkSelect && (
                  <label onClick={stop} className="flex items-center justify-center w-6 h-6 -ml-1 mt-1 flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelectCustomer?.(customer.id)}
                      className="h-[18px] w-[18px] rounded border-gray-300 cursor-pointer"
                    />
                  </label>
                )}
                <div className="w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300 flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                  {customer.type === 'organization' ? <BuildingOffice2Icon className="w-5 h-5" /> : initial}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-[15px] font-semibold text-gray-900 dark:text-gray-50 truncate leading-tight">{displayName(customer)}</span>
                    <span
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${customer.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}
                      title={customer.status === 'active' ? 'Aktív' : 'Inaktív'}
                    />
                    {customer.registrationSource === 'public_link' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-50 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300 flex-shrink-0">
                        <LinkIcon className="w-2.5 h-2.5 mr-0.5" />Link
                      </span>
                    )}
                  </div>
                  {customer.type === 'organization' && customer.taxNumber && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate tabular-nums">{customer.taxNumber}</div>
                  )}
                  {/* Elérhetőség */}
                  {(customer.email || customer.phone) && (
                    <div className="mt-1.5 flex flex-col gap-0.5 text-[13px]">
                      {customer.email && (
                        <a href={`mailto:${customer.email}`} onClick={stop} className="flex items-center text-gray-600 dark:text-gray-400 min-w-0">
                          <EnvelopeIcon className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
                          <span className="truncate">{customer.email}</span>
                        </a>
                      )}
                      <span onClick={stop}>
                        <PhoneCell phone={customer.phone} withIcon className="text-gray-600 dark:text-gray-400 max-w-full [&>svg]:mr-1.5" />
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Szervezetek és csoportok */}
              <div className="px-3 mt-2.5">
                <div className="flex flex-wrap gap-1.5">
                  {customer.organizations?.map(orgId => {
                    const org = organizations.find((o: SimpleOrganization) => o.id === orgId);
                    return org ? (
                      <span key={org.id} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-md text-xs font-medium bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300 max-w-full">
                        <BuildingOffice2Icon className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate max-w-[140px]">{org.name}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); onRemoveOrganization(customer, orgId); }}
                          aria-label="Eltávolítás a szervezetből"
                          className="ml-0.5 w-5 h-5 rounded flex items-center justify-center text-primary-500/70 hover:text-red-600 hover:bg-white/60 dark:hover:bg-black/20"
                        >
                          <XMarkIcon className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ) : null;
                  })}
                  {customerGroups.map(group => (
                    <span
                      key={group.id}
                      className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-md text-xs font-medium border max-w-full"
                      style={{
                        backgroundColor: group.color ? group.color + '1a' : 'rgba(59,130,246,0.1)',
                        borderColor: group.color ? group.color + '66' : '#3B82F6',
                        color: group.color || '#3B82F6'
                      }}
                    >
                      {group.icon && <span className="text-xs">{group.icon}</span>}
                      <span className="truncate max-w-[140px]">{group.name}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeCustomers(group.id, [customer.id]).then(() => {
                            toast.success('Ügyfél eltávolítva a csoportból');
                          }).catch((error: any) => {
                            toast.error(`Hiba: ${error.message}`);
                          });
                        }}
                        aria-label="Eltávolítás a csoportból"
                        className="ml-0.5 w-5 h-5 rounded flex items-center justify-center opacity-70 hover:opacity-100 hover:bg-white/60 dark:hover:bg-black/20"
                      >
                        <XMarkIcon className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                  <span className="contents" onClick={stop}>
                    <OrganizationAddButton
                      customer={customer}
                      organizations={organizations}
                      onAddOrganization={onAddOrganization}
                    />
                    <GroupAddButton customer={customer} />
                  </span>
                </div>
              </div>

              {/* Egyedi mezők */}
              {customFields.length > 0 && customFields.some(f => { const v = customer.customFields?.[f.key]; return !(v === undefined || v === null || v === ''); }) && (
                <div className="px-3 mt-2.5 space-y-1">
                  {customFields.map(field => {
                    const value = customer.customFields?.[field.key];
                    if (value === undefined || value === null || value === '') return null;
                    return (
                      <div key={field.id} className="flex items-start gap-2 text-xs">
                        <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">{field.name}:</span>
                        <span className="text-gray-800 dark:text-gray-200 min-w-0">
                          {Array.isArray(value) ? (
                            <span className="flex flex-wrap gap-1">
                              {value.map((val: string, idx: number) => (
                                <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800">{val}</span>
                              ))}
                            </span>
                          ) : field.type === 'checkbox' ? (
                            value ? 'igen' : 'nem'
                          ) : field.type === 'date' ? (
                            new Date(value).toLocaleDateString('hu-HU')
                          ) : (
                            String(value)
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {customer.notes && (
                <div className="px-3 mt-2.5 text-xs text-gray-600 dark:text-gray-400">
                  <div className="line-clamp-2">{customer.notes}</div>
                </div>
              )}

              {/* Akciósor – 44px érintőfelületek, felirattal */}
              <div className="mt-3 border-t border-[var(--crm-border)] grid grid-flow-col auto-cols-fr" onClick={stop}>
                <button onClick={() => onEditClick(customer)} className="crm-card-action">
                  <PencilSquareIcon /><span>Szerk.</span>
                </button>
                <button onClick={() => handleGenerateDocument(customer)} className="crm-card-action">
                  <DocumentPlusIcon /><span>Dok. gen.</span>
                </button>
                {customer.documents && customer.documents.length > 0 && (
                  <button onClick={() => onDocumentClick(customer)} className="crm-card-action">
                    <DocumentTextIcon /><span>Fájlok {customer.documents.length}</span>
                  </button>
                )}
                {onSendInvitation && (
                  <div className="crm-card-action">
                    <CustomerContactStatus customer={customer} onClick={() => onSendInvitation(customer)} />
                    <span>Meghívó</span>
                  </div>
                )}
                <button onClick={() => onStatusChange(customer)} className="crm-card-action">
                  {customer.status === 'active'
                    ? <CheckCircleIcon className="text-green-600 dark:text-green-400" />
                    : <XCircleIcon className="text-red-600 dark:text-red-400" />}
                  <span>{customer.status === 'active' ? 'Aktív' : 'Inaktív'}</span>
                </button>
                <button onClick={() => onDeleteClick(customer)} className="crm-card-action text-red-600 dark:text-red-400">
                  <TrashIcon /><span>Törlés</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default CustomerList;


